import { useCallback } from 'react';

import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import type {
	CommandConstructor,
	CommandProperties,
	CommandResult,
} from '@atlassian/rovo-content-bridge-api/command';
import type { CommandInvocation } from '@atlassian/rovo-content-bridge-api/command-invocation';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

// Helper type to make senderId optional in command parameters
type CommandParamsWithoutSenderId<T> = T extends { senderId: unknown }
	? Omit<T, 'senderId'> & { senderId?: T['senderId'] }
	: T;

export type SendCommand = <C extends CommandProperties, R extends CommandResult>(
	command: CommandConstructor<C, R>,
	params: CommandParamsWithoutSenderId<C>,
) => CommandInvocation<R>;

/**
 * Hook to send a command to the Bridge API with automatic senderId injection
 * @throws Error if used outside of BridgeAPIProvider
 */
export const useBridgeSendCommand = (): {
	sendCommand: SendCommand;
} => {
	const bridgeAPI = getBrowserBridgeAPI();

	const sendCommand: SendCommand = useCallback(
		<C extends CommandProperties, R extends CommandResult>(
			command: CommandConstructor<C, R>,
			params: CommandParamsWithoutSenderId<C>,
		) => {
			// Automatically inject senderId while preserving all other params
			const fullParams = {
				...params,
				senderId: ROVO_CHAT_CLIENT_ID,
			} as C;

			return bridgeAPI.send(command, fullParams);
		},
		[bridgeAPI],
	);

	return { sendCommand };
};
